import React, { useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { ApplicationState } from 'src/core/store'
import { getOrganizations } from 'src/core/store/ducks/organizations/actions'
import { Organization } from 'src/types'
import { Select } from 'src/components/Formik'

import { SelectProps, Option } from '../Select'
import { SelectFake } from '../select-fake'

interface Props extends Omit<SelectProps, 'name' | 'options'> {
  name?: string
}

function organizationSort(a: Organization, b: Organization): number {
  return `${a.trade_name}` < `${b.trade_name}` ? -1 : 1
}

function organizationMap(value: Organization): Option {
  const { organization_id, trade_name } = value
  return {
    label: `${trade_name}`,
    title: `${trade_name}`,
    value: `${organization_id}`
  }
}

export default function SelectOrganization({
  defaultActiveFirstOption,
  formikProps,
  label,
  name = 'organization_id',
  onChange,
  placeholder,
  required,
  ...props
}: Props) {
  const {
    organizations: { error, list, loading }
  } = useSelector((state: ApplicationState) => ({
    organizations: state.organizations
  }))

  const dispatch = useDispatch()

  useEffect(() => {
    if (!list.length) {
      dispatch(getOrganizations())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const options = useMemo<Option[]>(() => {
    if (error || loading) {
      return []
    }

    return list.sort(organizationSort).map(organizationMap)
  }, [loading, error, list])

  useEffect(() => {
    if (options.length && defaultActiveFirstOption) {
      formikProps.setFieldValue('organization_id', options[0].value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options])

  function handleChange() {
    if (onChange) {
      return function change(value: any, option: any) {
        onChange(value, option)
      }
    }
    return undefined
  }
  if (error) {
    const fakeOptions = {
      label: `Loading error`
    }

    return (
      <SelectFake
        label={label}
        fakeOptions={fakeOptions}
        placeholder={placeholder}
      />
    )
  }

  if (loading) {
    const fakeOptions = {
      label: ''
    }
    return (
      <SelectFake
        fakeOptions={fakeOptions}
        label={label}
        loading
        placeholder={placeholder}
        required={required}
      />
    )
  }

  return (
    <Select
      {...props}
      dataTestId="organizations-list"
      formikProps={formikProps}
      label={label}
      name={name}
      onChange={handleChange()}
      options={options}
      placeholder={placeholder}
      required={required}
    />
  )
}
